import { useEffect, useState } from 'react';
import { DialogBox } from '../../atomic/dialog';
import { tenantApi } from '../../../APIs/tenants';
import { projectApi } from '../../../APIs/projectApi';
import { mutateAsync, useGet } from '../../../hooks/useApi';
import { AiAnalysisType } from '../../../models/AiAnalysisType';
import { SlideSwitch } from '../../atomic/switches';
import { TenantModel, TenantOptionsModel } from '../../../models/TenantModel';
import { DialogView, useDialog } from '../../../hooks/useDialog';

export function useTenantEditDialog() {
    return useDialog(
        TenantEdit,
        (tenant: TenantModel) => ({
            options: { tenant },
            async onCommit(
                { updateAiTypes, tenantOptions, mosaicOptions, extApiOptions },
                { signal }
            ) {
                const { tenantId } = tenant;
                const result = await Promise.allSettled([
                    mutateAsync(
                        projectApi.mutate.updateAiType(tenantId),
                        {
                            aiAnalysisTypeIds: updateAiTypes,
                        },
                        { signal }
                    ),
                    mutateAsync(
                        tenantApi.mutate.updateTenantOption,
                        {
                            tenantId,
                            options: {
                                ...tenantOptions,
                                mosaic: {
                                    enable: mosaicOptions ? true : false,
                                },
                                extApi: {
                                    enable: extApiOptions ? true : false,
                                },
                            },
                        },
                        { signal }
                    ),
                ] as const);
                const errors = result.filter(
                    (entry) => entry.status === 'rejected'
                );
                if (errors.length > 0) {
                    throw new Error(
                        errors.map((error) => error.reason).join('\n')
                    );
                }
            },
        }),
        []
    );
}

const NOT_LOADED_AI_TYPES = Object.freeze([]) as any as string[];
const NOT_LOADED_TOPTS = Object.freeze({}) as {};

const TenantEdit: DialogView<
    { tenant: TenantModel },
    {
        updateAiTypes: string[];
        tenantOptions: TenantOptionsModel;
        mosaicOptions: boolean | null;
        extApiOptions: boolean | null;
    }
> = ({ options: { tenant }, onOK, loading, ...props }) => {
    const [mosaicOptions, setMosaicOptions] = useState(tenant.mosaic);
    const [extApiOptions, setExtApiOptions] = useState(tenant.extApi);
    const freshTenantOptions = useGet(
        tenantApi.get.kintoneOptions(tenant.tenantId)
    );
    const allTypes = useGet(projectApi.get.allAiAnalysisTypes);
    const tenantAiTypes = useGet(
        projectApi.get.tenantAiAnalysisTypes(tenant.tenantId)
    );
    const [updateAiTypes, setUpdateAiTypes] =
        useState<string[]>(NOT_LOADED_AI_TYPES);
    const [tenantOptions, setTenantOptions] =
        useState<TenantOptionsModel>(NOT_LOADED_TOPTS);
    loading =
        loading ||
        allTypes.isLoading ||
        tenantAiTypes.isLoading ||
        updateAiTypes === NOT_LOADED_AI_TYPES ||
        tenantOptions === NOT_LOADED_TOPTS;

    useEffect(() => {
        if (!freshTenantOptions.data) return;
        setTenantOptions(freshTenantOptions.data.data ?? {});
    }, [freshTenantOptions.data]);

    useEffect(() => {
        if (!tenantAiTypes.data?.data) return;
        setUpdateAiTypes(
            tenantAiTypes.data.data.map((entry) => entry.aiAnalysisTypeId)
        );
    }, [tenantAiTypes.data]);

    const onPreOk = () => {
        if (!tenantAiTypes.data) {
            return;
        }
        const deletedAi = tenantAiTypes.data?.data.some((ai) => {
            const result = updateAiTypes.some((update) => {
                return update === ai.aiAnalysisTypeId;
            });
            return !result;
        });
        if (
            deletedAi &&
            !confirm(
                'AIを変更するとプロジェクトやテンプレートが削除されますがよろしいですか？'
            )
        ) {
            return;
        }
        onOK({
            updateAiTypes,
            extApiOptions,
            mosaicOptions,
            tenantOptions,
        });
    };

    const onAiTypeToggle = (name: string) => {
        setUpdateAiTypes((prev) =>
            prev.includes(name)
                ? prev.filter((item) => item !== name)
                : [...prev, name]
        );
    };

    return (
        <DialogBox
            title="テナント編集"
            onOK={onPreOk}
            loading={loading}
            {...props}
        >
            <h2>{tenant?.name}</h2>
            <hr />
            <section>
                <h3>利用可能なAI</h3>
                <ul className="fields switch-list">
                    {updateAiTypes !== NOT_LOADED_AI_TYPES
                        ? (allTypes.data?.data ?? []).map(
                              (type: AiAnalysisType) => (
                                  <AiType
                                      key={type.aiAnalysisTypeId}
                                      type={type}
                                      onChange={onAiTypeToggle}
                                      checked={updateAiTypes.includes(
                                          type.aiAnalysisTypeId
                                      )}
                                  />
                              )
                          )
                        : null}
                </ul>
            </section>
            <section>
                <h3>オプション</h3>
                <ul className="fields switch-list">
                    {tenantOptions !== NOT_LOADED_TOPTS ? (
                        <li>
                            <SlideSwitch
                                text={'kintone連携'}
                                defaultValue={'kintone' in tenantOptions}
                                textPosition="left"
                                _onChange={(checked: boolean) => {
                                    if ('kintone' in tenantOptions) {
                                        if (!checked) {
                                            setTenantOptions((prevState) => {
                                                const nextState = prevState
                                                    ? { ...prevState }
                                                    : {};
                                                nextState['kintone'] = {};
                                                return nextState;
                                            });
                                        } else {
                                            setTenantOptions((prevState) => {
                                                const nextState = prevState
                                                    ? { ...prevState }
                                                    : {};
                                                nextState['kintone'] = {
                                                    id: '',
                                                    pass: '',
                                                    endpoint: '',
                                                };
                                                return nextState;
                                            });
                                        }
                                    } else {
                                        if (checked) {
                                            setTenantOptions((prevState) => {
                                                const nextState = prevState
                                                    ? { ...prevState }
                                                    : {};
                                                nextState['kintone'] = {
                                                    id: '',
                                                    pass: '',
                                                    endpoint: '',
                                                };
                                                return nextState;
                                            });
                                        }
                                    }
                                }}
                            />
                        </li>
                    ) : null}
                    <li>
                        <SlideSwitch
                            text={'モザイク設定'}
                            defaultValue={mosaicOptions ? true : false}
                            name={'option.kintone'}
                            textPosition="left"
                            _onChange={(checked: boolean) => {
                                setMosaicOptions(checked);
                            }}
                        />
                    </li>
                    <li>
                        <SlideSwitch
                            text={'外部API設定'}
                            defaultValue={extApiOptions ? true : false}
                            name={'option.kintone'}
                            textPosition="left"
                            _onChange={(checked: boolean) => {
                                setExtApiOptions(checked);
                            }}
                        />
                    </li>
                </ul>
            </section>
        </DialogBox>
    );
};

type AiProps = {
    type: AiAnalysisType;
    checked: boolean;
    onChange: (analysisTypeId: string) => void;
};

const AiType = (props: AiProps) => {
    const { type, checked, onChange } = props;

    const aiAnalysisTypeId = type.aiAnalysisTypeId;

    if (type.runnable === false && !checked) {
        return <></>;
    }

    return (
        <li className={type.runnable ? '' : 'disabled'}>
            <SlideSwitch
                disabled={!type.runnable}
                text={type.nameJa + (type.runnable ? '' : '(廃止)')}
                defaultValue={checked}
                name={type.aiAnalysisTypeId}
                textPosition="left"
                onChange={() => {
                    onChange(aiAnalysisTypeId);
                }}
            />
        </li>
    );
};
